import { getMfeByName, loadRemote } from "@repo/mfe-registry";
import type { MfeModule } from "@repo/mfe-registry";
import { Box, Loader } from "@saviynt/design-system";
import { Suspense, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

interface MfeLoaderProps {
  name: string;
  fallback?: React.ReactNode;
  props?: Record<string, unknown>;
  hostManagesLoading?: boolean;
}

const DefaultFallback = () => (
  <Box className="w-full h-full flex justify-center items-center">
    <Loader kind="loop" format="main" color="brand" />
  </Box>
);

export function MfeLoader({
  name,
  fallback = <DefaultFallback />,
  props = {},
  hostManagesLoading = false,
}: MfeLoaderProps) {
  const [Component, setComponent] = useState<React.ComponentType | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [key, setKey] = useState(0);

  useEffect(() => {
    setComponent(null);
    setError(null);

    const mfe = getMfeByName(name);
    if (!mfe) {
      setError(new Error(`MFE "${name}" not found in registry`));
      return;
    }

    loadRemote(name)
      .then((module: MfeModule) => {
        const Comp = module.default || module.App;
        if (Comp) {
          setComponent(() => Comp as React.ComponentType);
          if (import.meta.env.DEV) {
            setKey((k) => k + 1);
          }
        } else {
          setError(new Error("No component exported from remote"));
        }
      })
      .catch(setError);
  }, [name]);

  if (error) {
    return (
      <div className="p-4 text-[var(--color-foreground-critical-bold)]">
        <FormattedMessage
          id="KkbFrB"
          defaultMessage="Error loading remote: {message}"
          description="Error message when MFE fails to load"
          values={{ message: error.message }}
        />
      </div>
    );
  }

  if (!Component) {
    if (hostManagesLoading) {
      return null;
    }
    return <>{fallback}</>;
  }

  return (
    <Suspense fallback={null}>
      <Component key={key} {...props} />
    </Suspense>
  );
}

export function useDynamicRemote(name: string) {
  const [module, setModule] = useState<MfeModule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    loadRemote(name)
      .then((mod) => {
        setModule(mod);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  }, [name]);

  return { module, loading, error };
}
